import { defineMessages } from 'react-intl';

const messages = defineMessages({
  /* createUserWithEmailAndPassword */
  'auth/email-already-in-use': {
    id: 'errorCodes.firebase.auth/email-already-in-use',
    description:
      'Thrown if there already exists an account with the given email address.',
    defaultMessage: '이미 사용중인 이메일 입니다.'
  },
  'auth/invalid-email': {
    id: 'errorCodes.firebase.auth/invalid-email',
    description: 'Thrown if the email address is not valid.',
    defaultMessage: '올바르지 않은 이메일 형식입니다.'
  },
  'auth/operation-not-allowed': {
    id: 'errorCodes.firebase.auth/operation-not-allowed',
    description:
      'Thrown if email/password accounts are not enabled. Enable email/password accounts in the Firebase Console, under the Auth tab.',
    defaultMessage: '이메일 계정 성생에 에러가 발생 하였습니다.'
  },
  'auth/weak-password': {
    id: 'errorCodes.firebase.auth/weak-password',
    description: 'Thrown if the password is not strong enough.',
    defaultMessage: '비밀번호가 너무 약합니다.'
  },

  /* signInWithEmailAndPassword */
  'auth/user-disabled': {
    id: 'errorCodes.firebase.auth/user-disabled',
    description:
      'Thrown if the user corresponding to the given email has been disabled.',
    defaultMessage: '현재 사용이 불가능한 계정입니다.'
  },
  'auth/user-not-found': {
    id: 'errorCodes.firebase.auth/user-not-found',
    description: 'Thrown if there is no user corresponding to the given email.',
    defaultMessage: '퀀타스틱 계정을 찾을 수 없습니다.'
  },
  'auth/wrong-password': {
    id: 'errorCodes.firebase.auth/wrong-password',
    description:
      'Thrown if the password is invalid for the given email, or the account corresponding to the email does not have a password set.',
    defaultMessage:
      '잘못된 비밀번호입니다. 다시 시도하거나 비밀번호 찾기를 클릭하여 재설정하세요.'
  },

  /* verifyPhoneNumber */
  'auth/captcha-check-failed': {
    id: 'errorCodes.firebase.auth/captcha-check-failed',
    description:
      'Thrown if the reCAPTCHA response token was invalid, expired, or if this method was called from a non-whitelisted domain.',
    defaultMessage:
      '유효하지 않은 reCAPTCHA 토큰입니다. 잠시 후 다시 시도해 주세요.'
  },
  'auth/invalid-phone-number': {
    id: 'errorCodes.firebase.auth/invalid-phone-number',
    description: 'Thrown if the phone number has an invalid format.',
    defaultMessage: '올바르지 않은 핸드폰 번호입니다.'
  },
  'auth/missing-phone-number': {
    id: 'errorCodes.firebase.auth/missing-phone-number',
    description: 'Thrown if the phone number is missing.',
    defaultMessage: '핸드폰 번호는 필수 정보 입니다.'
  },
  'auth/quota-exceeded': {
    id: 'errorCodes.firebase.auth/quota-exceeded',
    description:
      'Thrown if the SMS quota for the Firebase project has been exceeded.',
    defaultMessage:
      'SMS 전송 에러가 발생하였습니다. 잠시 후 다시 시도해 주세요.'
  },

  /* PhoneAuthProvider credential */
  'auth/missing-verification-code': {
    id: 'errorCodes.firebase.auth/missing-verification-code',
    description: 'Thrown if the verification code is missing.',
    defaultMessage: '핸드폰 인증 번호를 입력해주세요.'
  },
  'auth/missing-verification-id': {
    id: 'errorCodes.firebase.auth/missing-verification-id',
    description: 'Thrown if the verification ID is missing.',
    defaultMessage: '핸드폰 인증 에러가 발생하였습니다.'
  },

  /* linkWithCredential */
  'auth/provider-already-linked': {
    id: 'errorCodes.firebase.auth/provider-already-linked',
    description:
      "Thrown if the provider has already been linked to the user. This error is thrown even if this is not the same provider's account that is currently linked to the user.",
    defaultMessage: '이미 계정연동이 완료된 인증입니다.'
  },
  'auth/invalid-credential': {
    id: 'errorCodes.firebase.auth/invalid-credential',
    description:
      "Thrown if the provider's credential is not valid. This can happen if it has already expired when calling link, or if it used invalid token(s). See the Firebase documentation for your provider, and make sure you pass in the correct parameters to the credential method.",
    defaultMessage: '만료된 인증입니다.'
  },
  'auth/credential-already-in-use': {
    id: 'errorCodes.firebase.auth/credential-already-in-use',
    description:
      'Thrown if the account corresponding to the credential already exists among your users, or is already linked to a Firebase User. For example, this error could be thrown if you are upgrading an anonymous user to a Google user by linking a Google credential to it and the Google credential used is already associated with an existing Firebase Google user. The fields error.email, error.phoneNumber, and error.credential (firebase.auth.AuthCredential) may be provided, depending on the type of credential. You can recover from this error by signing in with error.credential directly via firebase.auth.Auth.signInWithCredential.',
    defaultMessage: '이미 사용중인 계정이 있습니다.'
  },
  'auth/invalid-verification-code': {
    id: 'errorCodes.firebase.auth/invalid-verification-code',
    description:
      'Thrown if the credential is a firebase.auth.PhoneAuthProvider.credential and the verification code of the credential is not valid.',
    defaultMessage: '핸드폰 인증 번호가 올바르지 않습니다.'
  },
  'auth/invalid-verification-id': {
    id: 'errorCodes.firebase.auth/invalid-verification-id',
    description:
      'Thrown if the credential is a firebase.auth.PhoneAuthProvider.credential and the verification ID of the credential is not valid.',
    defaultMessage: '핸드폰 번호 인증 에러가 발생하였습니다.'
  },

  'auth/too-many-requests': {
    id: 'errorCodes.firebase.auth/too-many-requests',
    description:
      'Thrown if requests are blocked from a device due to unusual activity. Trying again after some delay would unblock.',
    defaultMessage:
      '비정상적인 요청으로 인해 요청이 차단되었습니다. 잠시후 다시 시도해 주세요.'
  },

  // https://firebase.google.com/docs/storage/web/handle-errors?hl=ko
  'storage/unknown': {
    id: 'errorCodes.firebase.storage/unknown',
    description: 'An unknown error occurred.',
    defaultMessage: '알 수 없는 오류가 발생했습니다.'
  },
  'storage/object-not-found': {
    id: 'errorCodes.firebase.storage/object-not-found',
    description: 'No object exists at the desired reference.',
    defaultMessage: '해당 참조에 개체가 없습니다.'
  },
  'storage/bucket-not-found': {
    id: 'errorCodes.firebase.storage/bucket-not-found',
    description: 'No bucket is configured for Cloud Storage',
    defaultMessage: 'Cloud Storage에 구성된 버킷이 없습니다.'
  },
  'storage/project-not-found': {
    id: 'errorCodes.firebase.storage/project-not-found',
    description: 'No project is configured for Cloud Storage',
    defaultMessage: 'Cloud Storage에 구성된 프로젝트가 없습니다.'
  },
  'storage/quota-exceeded': {
    id: 'errorCodes.firebase.storage/quota-exceeded',
    description: 'Quota on your Cloud Storage bucket has been exceeded.',
    defaultMessage: 'Cloud Storage 버킷의 할당량이 초과되었습니다.'
  },
  'storage/unauthenticated': {
    id: 'errorCodes.firebase.storage/unauthenticated',
    description: 'User is unauthenticated, please authenticate and try again.',
    defaultMessage:
      '사용자가 인증되지 않았습니다. 인증한 후 다시 시도해 보세요.'
  },
  'storage/unauthorized': {
    id: 'errorCodes.firebase.storage/unauthorized',
    description: 'Not authorized to perform the desired action',
    defaultMessage: '해당 작업을 수행할 권한이 없습니다.'
  },
  'storage/retry-limit-exceeded': {
    id: 'errorCodes.firebase.storage/retry-limit-exceeded',
    description:
      'The maximum time limit on an operation (upload, download, delete, etc.) has been excceded. Try uploading again.',
    defaultMessage:
      '작업(업로드, 다운로드, 삭제 등)의 최대 제한 시간이 초과되었습니다. 다시 업로드해 보세요.'
  },
  'storage/invalid-checksum': {
    id: 'errorCodes.firebase.storage/invalid-checksum',
    description:
      'File on the client does not match the checksum of the file received by the server. Try uploading again.',
    defaultMessage:
      '클라이언트의 파일과 서버에서 수신한 파일의 체크섬이 일치하지 않습니다. 다시 업로드해 보세요.'
  },
  'storage/canceled': {
    id: 'errorCodes.firebase.storage/canceled',
    description: 'User canceled the operation.',
    defaultMessage: '사용자가 작업을 취소했습니다.'
  },
  'storage/invalid-event-name': {
    id: 'errorCodes.firebase.storage/invalid-event-name',
    description:
      'Invalid event name provided. Must be one of [`running`, `progress`, `pause`]',
    defaultMessage:
      '이벤트 이름이 잘못되었습니다. 다음 중 하나여야 합니다. [`running`, `progress`, `pause`]'
  },
  'storage/invalid-url': {
    id: 'errorCodes.firebase.storage/invalid-url',
    description: 'Invalid URL provided to refFromURL().',
    defaultMessage: 'refFromURL()에 제공된 URL이 잘못되었습니다.'
  },
  'storage/invalid-argument': {
    id: 'errorCodes.firebase.storage/invalid-argument',
    description:
      'The argument passed to put() must be `File`, `Blob`, or `UInt8` Array. The argument passed to putString() must be a raw, `Base64`, or `Base64URL` string.',
    defaultMessage:
      "put()에 전달된 인수는 'File', 'Blob' 또는 'UInt8' 배열이어야 합니다. putString()에 전달된 인수는 원시, 'Base64' 또는 'Base64URL' 문자열이어야 합니다."
  },
  'storage/no-default-bucket': {
    id: 'errorCodes.firebase.storage/no-default-bucket',
    description:
      "No bucket has been set in your config's storageBucket property.",
    defaultMessage: '구성의 storageBucket 속성에 버킷이 설정되지 않았습니다.'
  },
  'storage/cannot-slice-blob': {
    id: 'errorCodes.firebase.storage/cannot-slice-blob',
    description:
      "Commonly occurs when the local file has changed (deleted, saved again, etc.). Try uploading again after verifying that the file hasn't changed.",
    defaultMessage:
      '로컬 파일이 변경(삭제, 재저장 등)되었을 때 흔히 발생합니다. 파일이 변경되지 않았는지 확인한 후 다시 업로드해 보세요.'
  },
  'storage/server-file-wrong-size': {
    id: 'errorCodes.firebase.storage/server-file-wrong-size',
    description:
      'File on the client does not match the size of the file recieved by the server. Try uploading again.',
    defaultMessage:
      '클라이언트의 파일과 서버에서 수신한 파일의 크기가 일치하지 않습니다. 다시 업로드해 보세요.'
  },
  unknown: {
    id: 'errorCodes.unknown',
    description: 'Unknow error',
    defaultMessage: '알수 없는 에러가 발생 하였습니다.'
  }
});

export default messages;
